import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Instagram, Mail, MapPin } from "lucide-react";
import { Eyebrow, Kinetic, Reveal } from "@/components/anim";
import { api, formatApiError } from "@/lib/api";
import { CONTACT } from "@/lib/nav";

const RENTAL_TYPES = [
  "Pop-Up Shop",
  "Weekend Takeover",
  "Weekly Retail Rental",
  "Monthly Retail Residency",
  "Rack Rental",
  "Shelf Rental",
  "Makeup Chair",
  "Hair Chair",
  "Nail Station",
  "Beauty Flex Pass",
  "Product Launch",
  "Private Event",
  "Creative Space",
];

const INITIAL = {
  full_name: "",
  business_name: "",
  email: "",
  phone: "",
  instagram: "",
  website: "",
  business_type: "",
  products_services: "",
  rental_type: "",
  requested_dates: "",
  rental_length: "",
  expected_customers: "",
  special_requirements: "",
  additional_services: "",
};

export default function Book() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const presetType = params.get("type") || "";

  useEffect(() => {
    if (presetType && RENTAL_TYPES.includes(presetType)) {
      setForm((f) => ({ ...f, rental_type: presetType }));
    }
  }, [presetType]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      const { data } = await api.post("/inquiries", form);
      setDone(data);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const field = (key, props = {}) => ({
    className: "field",
    value: form[key],
    onChange: set(key),
    "data-testid": `booking-${key.replace(/_/g, "-")}-input`,
    ...props,
  });

  if (done) {
    return (
      <section className="bg-paper pb-32 pt-40 sm:pt-48" data-testid="booking-success">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <Reveal>
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blush">
              <CheckCircle2 size={26} strokeWidth={1.5} className="text-ink" />
            </span>
            <h1 className="mt-8 font-serif text-4xl uppercase text-ink sm:text-5xl">
              Request <em>received.</em>
            </h1>
            <p className="mt-6 text-sm leading-relaxed text-clay sm:text-base">
              Thank you, {form.full_name}. Your inquiry for a{" "}
              <strong className="text-ink">{form.rental_type}</strong> is in our inbox. Requesting
              a date does not guarantee availability — Essandtials will contact you to confirm
              your booking.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/" className="btn btn-dark">
                Back Home
              </Link>
              <a
                href={CONTACT.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline"
              >
                Follow @essandtials
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="relative overflow-hidden bg-paper pb-20 pt-36 sm:pt-44" data-testid="book-page">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-40 top-20 h-[420px] w-[420px] rounded-full bg-blush/50 blur-3xl"
        />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
          <Reveal delay={0.1}>
            <Eyebrow>Book Your Space</Eyebrow>
          </Reveal>
          <Kinetic
            delay={0.2}
            lines={[
              <>Request</>,
              <>
                your <em>space.</em>
              </>,
            ]}
            className="mt-6 font-serif text-5xl font-light uppercase leading-[0.98] text-ink sm:text-7xl"
          />
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-clay sm:text-base">
              Tell us about your business and what you&rsquo;re planning. We&rsquo;ll reply with
              availability, pricing, and next steps.
            </p>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 gap-14 lg:grid-cols-12">
            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <div className="rounded-3xl border border-beige/60 bg-sand/70 p-8">
                <p className="text-[11px] uppercase tracking-[0.28em] text-fog">Reach us directly</p>
                <ul className="mt-5 space-y-4 text-sm">
                  <li>
                    <a
                      href={`mailto:${CONTACT.email}`}
                      data-testid="book-contact-email"
                      className="flex items-center gap-3 text-ink transition-colors hover:text-sanddeep"
                    >
                      <Mail size={15} strokeWidth={1.5} className="text-sanddeep" />
                      {CONTACT.email}
                    </a>
                  </li>
                  <li>
                    <a
                      href={CONTACT.instagramUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="book-contact-instagram"
                      className="flex items-center gap-3 text-ink transition-colors hover:text-sanddeep"
                    >
                      <Instagram size={15} strokeWidth={1.5} className="text-sanddeep" />
                      @essandtials
                    </a>
                  </li>
                  <li className="flex items-start gap-3 text-ink">
                    <MapPin size={15} strokeWidth={1.5} className="mt-0.5 text-sanddeep" />
                    <span>{CONTACT.address}</span>
                  </li>
                </ul>
                <div className="mt-8 border-t border-beige/70 pt-6">
                  <p className="text-[11px] uppercase tracking-[0.28em] text-fog">Hours</p>
                  <ul className="mt-3 space-y-1.5 text-sm text-clay">
                    {CONTACT.hours.map((h) => (
                      <li key={h.days} className="flex justify-between gap-3">
                        <span>{h.days}</span>
                        <span className="text-ink">{h.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <p className="mt-6 rounded-2xl border border-blushdeep bg-blush/50 p-5 text-xs leading-relaxed text-clay" data-testid="booking-availability-note">
                Requesting a date does not guarantee availability. Essandtials will contact you to
                confirm your booking.
              </p>
            </aside>

            {/* Form */}
            <form
              onSubmit={submit}
              className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-8"
              data-testid="booking-inquiry-form"
            >
              <div>
                <label className="lbl" htmlFor="full_name">Full Name *</label>
                <input id="full_name" required {...field("full_name")} />
              </div>
              <div>
                <label className="lbl" htmlFor="business_name">Business Name</label>
                <input id="business_name" {...field("business_name")} />
              </div>
              <div>
                <label className="lbl" htmlFor="email">Email *</label>
                <input id="email" type="email" required {...field("email")} />
              </div>
              <div>
                <label className="lbl" htmlFor="phone">Phone / WhatsApp *</label>
                <input id="phone" required {...field("phone")} />
              </div>
              <div>
                <label className="lbl" htmlFor="instagram">Instagram</label>
                <input id="instagram" placeholder="@yourbrand" {...field("instagram")} />
              </div>
              <div>
                <label className="lbl" htmlFor="website">Website</label>
                <input id="website" {...field("website")} />
              </div>
              <div>
                <label className="lbl" htmlFor="business_type">Business Type</label>
                <input id="business_type" {...field("business_type")} />
              </div>
              <div>
                <label className="lbl" htmlFor="products_services">Products or Services Offered</label>
                <input id="products_services" {...field("products_services")} />
              </div>
              <div>
                <label className="lbl" htmlFor="rental_type">Requested Rental Type *</label>
                <select id="rental_type" required {...field("rental_type")}>
                  <option value="" disabled>
                    Select a rental type
                  </option>
                  {RENTAL_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="lbl" htmlFor="requested_dates">Requested Dates</label>
                <input id="requested_dates" placeholder="e.g. June 14 – 16" {...field("requested_dates")} />
              </div>
              <div>
                <label className="lbl" htmlFor="rental_length">Preferred Rental Length</label>
                <input id="rental_length" placeholder="e.g. 1 day, 1 week, 1 month" {...field("rental_length")} />
              </div>
              <div>
                <label className="lbl" htmlFor="expected_customers">
                  Expected Number of Customers or Clients
                </label>
                <input id="expected_customers" {...field("expected_customers")} />
              </div>
              <div className="sm:col-span-2">
                <label className="lbl" htmlFor="special_requirements">Special Requirements</label>
                <textarea id="special_requirements" rows={4} {...field("special_requirements")} />
              </div>
              <div className="sm:col-span-2">
                <label className="lbl" htmlFor="additional_services">
                  Additional Services Needed (visual merchandising, photography, marketing…)
                </label>
                <textarea id="additional_services" rows={3} {...field("additional_services")} />
              </div>

              {error && (
                <p className="sm:col-span-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="booking-form-error">
                  {error}
                </p>
              )}

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="request-space-button"
                  className="btn btn-dark w-full sm:w-auto disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Request Your Space"}
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
